import React from 'react';
import { observer } from 'mobx-react';
import { action, makeObservable, observable } from 'mobx';
import { ConfigProvider, Table, Tabs, Typography, Spin, Alert, theme, Row, Col, Segmented, InputNumber, Radio } from 'antd';
import { EconomyProxy } from '../services/catalogServiceAPI/economyProxy';
import './economy.css';

const { Title } = Typography;

function buildCategoryPath(categoryId, categoryMap, depth = 0) {
    if (!categoryId || depth > 10) return '';
    const cat = categoryMap.get(categoryId);
    if (!cat) return '';
    const parentPath = cat.parentId ? buildCategoryPath(cat.parentId, categoryMap, depth + 1) : '';
    return parentPath ? `${parentPath} > ${cat.title}` : cat.title;
}

function pct(count, total) {
    if (total === 0) return '—';
    return (count / total * 100).toFixed(1) + '%';
}

const sorterNum = (key) => (a, b) => (a[key] ?? -1) - (b[key] ?? -1);
const sorterStr = (key) => (a, b) => (a[key] ?? '').localeCompare(b[key] ?? '');
const sorterPct = (key) => (a, b) => {
    const av = a[key] === '—' ? -1 : parseFloat(a[key]);
    const bv = b[key] === '—' ? -1 : parseFloat(b[key]);
    return av - bv;
};

const TIER_COLUMNS = [
    { title: 'Category', dataIndex: 'path', sorter: sorterStr('path'), defaultSortOrder: 'ascend', width: 280 },
    { title: 'Free', dataIndex: 'freeCount', sorter: sorterNum('freeCount'), align: 'right' },
    { title: 'Premium', dataIndex: 'premiumCount', sorter: sorterNum('premiumCount'), align: 'right' },
    { title: 'Unlockable', dataIndex: 'unlockableCount', sorter: sorterNum('unlockableCount'), align: 'right' },
    { title: 'Free %', dataIndex: 'freePercent', sorter: sorterPct('freePercent'), align: 'right' },
    { title: 'Premium %', dataIndex: 'premiumPercent', sorter: sorterPct('premiumPercent'), align: 'right' },
    { title: 'Unlockable %', dataIndex: 'unlockablePercent', sorter: sorterPct('unlockablePercent'), align: 'right' },
];

const PRICE_COLUMNS = [
    { title: 'Category', dataIndex: 'path', sorter: sorterStr('path'), defaultSortOrder: 'ascend', width: 280 },
    { title: 'Min Price', dataIndex: 'minCoinPrice', sorter: sorterNum('minCoinPrice'), align: 'right', render: v => v ?? '—' },
    { title: 'Max Price', dataIndex: 'maxCoinPrice', sorter: sorterNum('maxCoinPrice'), align: 'right', render: v => v ?? '—' },
    { title: 'Low 33% Avg', dataIndex: 'low33Avg', sorter: sorterNum('low33Avg'), align: 'right', render: v => v ?? '—' },
    { title: 'Mid 33% Avg', dataIndex: 'mid33Avg', sorter: sorterNum('mid33Avg'), align: 'right', render: v => v ?? '—' },
    { title: 'High 33% Avg', dataIndex: 'high33Avg', sorter: sorterNum('high33Avg'), align: 'right', render: v => v ?? '—' },
];

const SEGMENT_LABELS = { power: 'Power', mid: 'Mid', min: 'Min' };
const SEGMENT_HINTS = {
    power: '5+ challenges or streak > 7',
    mid: '1+ challenge or streak > 2',
    min: 'everyone else',
};

const fmt = (v) => (v === null || v === undefined ? '—' : Math.round(v).toLocaleString());

const balanceColumnGroup = (currency, label) => ({
    title: label,
    children: ['avg', 'p25', 'p50', 'p75', 'p90'].map(k => ({
        title: k === 'avg' ? 'Avg' : k.toUpperCase(),
        dataIndex: `${currency}_${k}`,
        align: 'right',
        render: fmt,
    })),
});

const BALANCE_COLUMNS = [
    { title: 'Segment', dataIndex: 'segmentLabel', width: 120 },
    balanceColumnGroup('coin', 'Coins'),
    balanceColumnGroup('gem', 'Gems'),
];

const OWNED_COLUMNS = [
    { title: 'Segment', dataIndex: 'segmentLabel', width: 120 },
    { title: 'Owners', dataIndex: 'ownedOwners', align: 'right', render: fmt },
    { title: 'Owners %', dataIndex: 'ownedPctOwners', align: 'right', render: v => `${v}%` },
    { title: 'Total Value', dataIndex: 'ownedTotalValue', align: 'right', render: fmt },
    { title: 'Avg / owner', dataIndex: 'ownedAvg', align: 'right', render: fmt },
    { title: 'P25', dataIndex: 'ownedP25', align: 'right', render: fmt },
    { title: 'P50', dataIndex: 'ownedP50', align: 'right', render: fmt },
    { title: 'P75', dataIndex: 'ownedP75', align: 'right', render: fmt },
    { title: 'P90', dataIndex: 'ownedP90', align: 'right', render: fmt },
];

const ENGAGEMENT_COLUMNS = [
    { title: 'Segment', dataIndex: 'segmentLabel', width: 120 },
    { title: 'Coins / active day (median)', dataIndex: 'coinEarnRate', align: 'right', render: fmt },
    { title: 'Gems / active day (median)', dataIndex: 'gemEarnRate', align: 'right', render: fmt },
    { title: 'Spent anything (30d)', dataIndex: 'pctWithSpend30d', align: 'right', render: v => `${v}%` },
    { title: 'Bought a pack (ever)', dataIndex: 'pctPayerEver', align: 'right', render: v => `${v}%` },
    { title: 'Unlocks (90d)', dataIndex: 'unlockCount', align: 'right', render: fmt },
    { title: 'Unlock price P25', dataIndex: 'unlockP25', align: 'right', render: fmt },
    { title: 'Unlock price P50', dataIndex: 'unlockP50', align: 'right', render: fmt },
    { title: 'Unlock price P75', dataIndex: 'unlockP75', align: 'right', render: fmt },
];

function buildFlowRows(population) {
    const byKey = new Map();
    for (const seg of population.segments) {
        for (const currency of ['coin', 'gem']) {
            for (const flow of seg[currency].flows) {
                const key = `${flow.type}|${currency}`;
                if (!byKey.has(key)) byKey.set(key, { key, type: flow.type, currency });
                const row = byKey.get(key);
                row[`${seg.segment}_earned`] = (row[`${seg.segment}_earned`] ?? 0) + flow.earned;
                row[`${seg.segment}_spent`] = (row[`${seg.segment}_spent`] ?? 0) + flow.spent;
            }
        }
    }
    const magnitude = (r) => ['power', 'mid', 'min']
        .reduce((s, seg) => s + Math.abs(r[`${seg}_earned`] ?? 0) + Math.abs(r[`${seg}_spent`] ?? 0), 0);
    return Array.from(byKey.values()).sort((a, b) => magnitude(b) - magnitude(a));
}

const FLOW_COLUMNS = [
    { title: 'Transaction Type', dataIndex: 'type', width: 200 },
    {
        title: 'Currency', dataIndex: 'currency', width: 90,
        render: v => <span className={`economy-currency-badge economy-currency-badge--${v}`}>{v}</span>,
    },
    ...['power', 'mid', 'min'].map(seg => ({
        title: SEGMENT_LABELS[seg],
        children: [
            { title: 'Earned', dataIndex: `${seg}_earned`, align: 'right', render: v => (v ? fmt(v) : '—') },
            { title: 'Spent', dataIndex: `${seg}_spent`, align: 'right', render: v => (v ? fmt(v) : '—') },
        ],
    })),
];

function affordability(seg, currency, price) {
    if (!seg.userCount || !seg[currency].balancePercentiles.length) {
        return { pctAfford: null, daysToEarn: null };
    }
    const percentiles = seg[currency].balancePercentiles;
    const pctAfford = percentiles.filter(v => v >= price).length / percentiles.length * 100;
    const rate = seg[currency].medianEarnPerActiveDay;
    const daysToEarn = rate
        ? Math.ceil(Math.max(0, price - seg[currency].balance.p50) / rate)
        : null;
    return { pctAfford, daysToEarn };
}

@observer
export class Economy extends React.Component {
    economyProxy = new EconomyProxy();

    @observable rows = [];
    @observable isLoading = false;
    @observable error = null;

    @observable walletsReport = null;
    @observable isLoadingWallets = false;
    @observable walletsError = null;
    @observable walletsPopulation = 'active30d';
    @observable walletsAudience = 'all';
    @observable simPrice = 1000;
    @observable simCurrency = 'coin';

    @action.bound setRows = (rows) => { this.rows = rows; };
    @action.bound setIsLoading = (v) => { this.isLoading = v; };
    @action.bound setError = (e) => { this.error = e; };

    @action.bound setWalletsReport = (r) => { this.walletsReport = r; };
    @action.bound setIsLoadingWallets = (v) => { this.isLoadingWallets = v; };
    @action.bound setWalletsError = (e) => { this.walletsError = e; };
    @action.bound setWalletsPopulation = (v) => { this.walletsPopulation = v; };
    @action.bound setWalletsAudience = (v) => { this.walletsAudience = v; };
    @action.bound setSimPrice = (v) => { this.simPrice = v ?? 0; };
    @action.bound setSimCurrency = (v) => { this.simCurrency = v; };

    constructor(props) {
        super(props);
        makeObservable(this);
    }

    componentDidMount() {
        this.fetch();
        this.fetchWallets();
    }

    async fetch() {
        this.setIsLoading(true);
        this.setError(null);
        try {
            const result = await this.economyProxy.getEconomy();
            const categoryMap = new Map(
                result.categories.map(c => [c.categoryId, { title: c.title, parentId: c.parentId }])
            );
            const rows = result.categories.map(c => {
                const total = c.freeCount + c.premiumCount + c.unlockableCount;
                return {
                    key: c.categoryId,
                    path: buildCategoryPath(c.categoryId, categoryMap),
                    freeCount: c.freeCount,
                    premiumCount: c.premiumCount,
                    unlockableCount: c.unlockableCount,
                    freePercent: pct(c.freeCount, total),
                    premiumPercent: pct(c.premiumCount, total),
                    unlockablePercent: pct(c.unlockableCount, total),
                    minCoinPrice: c.minCoinPrice,
                    maxCoinPrice: c.maxCoinPrice,
                    low33Avg: c.low33Avg,
                    mid33Avg: c.mid33Avg,
                    high33Avg: c.high33Avg,
                };
            });
            this.setRows(rows);
        } catch (err) {
            this.setError(err.message || 'Failed to load economy data');
        } finally {
            this.setIsLoading(false);
        }
    }

    async fetchWallets() {
        this.setIsLoadingWallets(true);
        this.setWalletsError(null);
        try {
            this.setWalletsReport(await this.economyProxy.getWallets());
        } catch (err) {
            this.setWalletsError(err.message || 'Failed to load wallet data');
        } finally {
            this.setIsLoadingWallets(false);
        }
    }

    renderWalletsTab() {
        if (this.walletsError) {
            return <Alert type="error" message={this.walletsError} />;
        }
        if (!this.walletsReport) {
            return <Spin spinning={this.isLoadingWallets}><div style={{ minHeight: 200 }} /></Spin>;
        }

        const audiencePrefix = { premium: 'premium', free: 'free' }[this.walletsAudience];
        const populationKey = audiencePrefix
            ? `${audiencePrefix}${this.walletsPopulation === 'all' ? 'All' : 'Active30d'}`
            : this.walletsPopulation;
        const population = this.walletsReport.populations[populationKey];
        const segmentRows = population.segments.map(s => ({
            key: s.segment,
            segmentLabel: SEGMENT_LABELS[s.segment],
            coin_avg: s.coin.balance.avg,
            coin_p25: s.coin.balance.p25,
            coin_p50: s.coin.balance.p50,
            coin_p75: s.coin.balance.p75,
            coin_p90: s.coin.balance.p90,
            gem_avg: s.gem.balance.avg,
            gem_p25: s.gem.balance.p25,
            gem_p50: s.gem.balance.p50,
            gem_p75: s.gem.balance.p75,
            gem_p90: s.gem.balance.p90,
            coinEarnRate: s.coin.medianEarnPerActiveDay,
            gemEarnRate: s.gem.medianEarnPerActiveDay,
            pctWithSpend30d: s.pctWithSpend30d,
            pctPayerEver: s.pctPayerEver,
            unlockCount: s.unlockSpend90d.count,
            unlockP25: s.unlockSpend90d.p25,
            unlockP50: s.unlockSpend90d.p50,
            unlockP75: s.unlockSpend90d.p75,
            ownedOwners: s.ownedItems.owners,
            ownedPctOwners: s.ownedItems.pctOwners,
            ownedTotalValue: s.ownedItems.totalValue,
            ownedAvg: s.ownedItems.value.avg,
            ownedP25: s.ownedItems.value.p25,
            ownedP50: s.ownedItems.value.p50,
            ownedP75: s.ownedItems.value.p75,
            ownedP90: s.ownedItems.value.p90,
        }));

        return (
            <>
                <div className="economy-wallets-header">
                    <Segmented
                        value={this.walletsAudience}
                        onChange={this.setWalletsAudience}
                        options={[
                            { label: 'All users', value: 'all' },
                            { label: `Premium only (${(this.walletsReport.premiumUserCount ?? 0).toLocaleString()})`, value: 'premium' },
                            { label: 'Free only', value: 'free' },
                        ]}
                    />
                    <Segmented
                        value={this.walletsPopulation}
                        onChange={this.setWalletsPopulation}
                        options={[
                            { label: 'Active last 30 days', value: 'active30d' },
                            { label: 'All wallets', value: 'all' },
                        ]}
                    />
                </div>
                {this.walletsAudience !== 'all' && (
                    <div className="economy-footnote" style={{ marginTop: -8, marginBottom: 12, textAlign: 'right' }}>
                        {this.walletsAudience === 'premium'
                            ? 'Premium = users with a currently active or trialing subscription (admins excluded).'
                            : 'Free = users without an active or trialing subscription.'}
                    </div>
                )}

                <Row gutter={16} className="economy-stats-row">
                    {population.segments.map(s => (
                        <Col span={8} key={s.segment}>
                            <div className={`economy-stat-card economy-stat-card--${s.segment}`}>
                                <div className="economy-stat-label">{SEGMENT_LABELS[s.segment]}</div>
                                <div className="economy-stat-value">{s.userCount.toLocaleString()}</div>
                                <div className="economy-stat-pct">{s.pctOfTotal}% · {SEGMENT_HINTS[s.segment]}</div>
                            </div>
                        </Col>
                    ))}
                </Row>

                <div className="economy-section-title">Wallet Balances</div>
                <Table
                    dataSource={segmentRows}
                    columns={BALANCE_COLUMNS}
                    pagination={false}
                    size="small"
                    scroll={{ x: true }}
                />
                <div className="economy-footnote">
                    Gems = permanent + legacy recharged balance. Balances are a point-in-time snapshot.
                </div>

                <div className="economy-section-title">Owned Items Value (all-time)</div>
                <Table
                    dataSource={segmentRows}
                    columns={OWNED_COLUMNS}
                    pagination={false}
                    size="small"
                    scroll={{ x: true }}
                    summary={(rows) => {
                        const owners = rows.reduce((s, r) => s + r.ownedOwners, 0);
                        const total = rows.reduce((s, r) => s + r.ownedTotalValue, 0);
                        return (
                            <Table.Summary.Row>
                                <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
                                <Table.Summary.Cell index={1} align="right">{fmt(owners)}</Table.Summary.Cell>
                                <Table.Summary.Cell index={2} />
                                <Table.Summary.Cell index={3} align="right">{fmt(total)}</Table.Summary.Cell>
                                <Table.Summary.Cell index={4} colSpan={5} />
                            </Table.Summary.Row>
                        );
                    }}
                />
                <div className="economy-footnote">
                    Coin value of every catalog item users have bought (unlocked with coins), across all time.
                </div>

                <div className="economy-section-title">Earning &amp; Spending</div>
                <Table
                    dataSource={segmentRows}
                    columns={ENGAGEMENT_COLUMNS}
                    pagination={false}
                    size="small"
                    scroll={{ x: true }}
                />

                <div className="economy-section-title">Sources &amp; Sinks (last {this.walletsReport.activityWindowDays} days)</div>
                <Table
                    dataSource={buildFlowRows(population)}
                    columns={FLOW_COLUMNS}
                    pagination={false}
                    size="small"
                    scroll={{ x: true }}
                />

                <div className="economy-section-title">Price Simulator</div>
                <div className="economy-sim-card">
                    <div className="economy-sim-controls">
                        <Radio.Group
                            value={this.simCurrency}
                            onChange={e => this.setSimCurrency(e.target.value)}
                            options={[
                                { label: 'Coins', value: 'coin' },
                                { label: 'Gems', value: 'gem' },
                            ]}
                            optionType="button"
                        />
                        <InputNumber
                            value={this.simPrice}
                            onChange={this.setSimPrice}
                            min={0}
                            step={this.simCurrency === 'coin' ? 100 : 5}
                            style={{ width: 140 }}
                        />
                    </div>
                    <Row gutter={16}>
                        {population.segments.map(s => {
                            const { pctAfford, daysToEarn } = affordability(s, this.simCurrency, this.simPrice);
                            return (
                                <Col span={8} key={s.segment}>
                                    <div className={`economy-stat-card economy-stat-card--${s.segment}`}>
                                        <div className="economy-stat-label">{SEGMENT_LABELS[s.segment]}</div>
                                        <div className="economy-stat-value">
                                            {pctAfford === null ? '—' : `${pctAfford.toFixed(0)}%`}
                                        </div>
                                        <div className="economy-stat-pct">
                                            can afford now · {daysToEarn === null ? '—' : `~${daysToEarn}d`} for median user to earn
                                        </div>
                                    </div>
                                </Col>
                            );
                        })}
                    </Row>
                </div>
            </>
        );
    }

    render() {
        return (
            <ConfigProvider
                theme={{
                    algorithm: theme.darkAlgorithm,
                    token: {
                        // Solid dark base so FastColor blending resolves correctly
                        colorBgContainer: '#1e1a3a',
                        colorBgElevated: '#261f4a',
                        colorBorderSecondary: 'rgba(255, 255, 255, 0.12)',
                        colorBorder: 'rgba(255, 255, 255, 0.15)',
                        colorText: 'rgba(255, 255, 255, 0.92)',
                        colorTextSecondary: 'rgba(255, 255, 255, 0.60)',
                        colorPrimary: '#7b6ef6',
                        borderRadius: 12,
                        fontFamily: 'inherit',
                    },
                    components: {
                        Table: {
                            headerBg: '#2a2550',
                            headerColor: 'rgba(255, 255, 255, 0.92)',
                            headerSortActiveBg: '#2f2a58',
                            headerSortHoverBg: '#342f60',
                            bodySortBg: '#221e42',
                            rowHoverBg: '#261f4a',
                            colorBgContainer: '#1e1a3a',
                            borderColor: 'rgba(255, 255, 255, 0.10)',
                        },
                        Tabs: {
                            inkBarColor: '#7b6ef6',
                            itemActiveColor: '#fff',
                            itemSelectedColor: '#fff',
                            itemColor: 'rgba(255,255,255,0.55)',
                        },
                    },
                }}
            >
                <div className="economy-page">
                    <Title level={2} className="economy-title">Economy</Title>

                    {this.error && (
                        <Alert type="error" message={this.error} style={{ marginBottom: 16 }} />
                    )}

                    <Spin spinning={this.isLoading}>
                        <div className="economy-glass-card">
                            <Tabs
                                items={[
                                    {
                                        key: 'tiers',
                                        label: 'Tier Distribution',
                                        children: (() => {
                                            const totalFree = this.rows.reduce((s, r) => s + r.freeCount, 0);
                                            const totalPremium = this.rows.reduce((s, r) => s + r.premiumCount, 0);
                                            const totalUnlockable = this.rows.reduce((s, r) => s + r.unlockableCount, 0);
                                            const grand = totalFree + totalPremium + totalUnlockable;
                                            const p = (n) => grand > 0 ? (n / grand * 100).toFixed(1) + '%' : '—';
                                            return (
                                                <>
                                                    <Row gutter={16} className="economy-stats-row">
                                                        <Col span={8}>
                                                            <div className="economy-stat-card">
                                                                <div className="economy-stat-label">Free</div>
                                                                <div className="economy-stat-value">{totalFree.toLocaleString()}</div>
                                                                <div className="economy-stat-pct">{p(totalFree)}</div>
                                                            </div>
                                                        </Col>
                                                        <Col span={8}>
                                                            <div className="economy-stat-card economy-stat-card--unlockable">
                                                                <div className="economy-stat-label">Unlockable</div>
                                                                <div className="economy-stat-value">{totalUnlockable.toLocaleString()}</div>
                                                                <div className="economy-stat-pct">{p(totalUnlockable)}</div>
                                                            </div>
                                                        </Col>
                                                        <Col span={8}>
                                                            <div className="economy-stat-card economy-stat-card--premium">
                                                                <div className="economy-stat-label">Premium</div>
                                                                <div className="economy-stat-value">{totalPremium.toLocaleString()}</div>
                                                                <div className="economy-stat-pct">{p(totalPremium)}</div>
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                    <Table
                                                        dataSource={this.rows}
                                                        columns={TIER_COLUMNS}
                                                        pagination={false}
                                                        size="small"
                                                        scroll={{ x: true }}
                                                        showSorterTooltip={false}
                                                    />
                                                </>
                                            );
                                        })(),
                                    },
                                    {
                                        key: 'prices',
                                        label: 'Unlockable Price Stats',
                                        children: (
                                            <Table
                                                dataSource={this.rows.filter(r => r.unlockableCount > 0)}
                                                columns={PRICE_COLUMNS}
                                                pagination={false}
                                                size="small"
                                                scroll={{ x: true }}
                                                showSorterTooltip={false}
                                            />
                                        ),
                                    },
                                    {
                                        key: 'wallets',
                                        label: 'Wallets & Pricing',
                                        children: this.renderWalletsTab(),
                                    },
                                ]}
                            />
                        </div>
                    </Spin>
                </div>
            </ConfigProvider>
        );
    }
}
